import { createRoot } from 'react-dom/client';
import { Documents } from '../../components/Documents';
import { OrderNumberComponent } from '../../components/OrderNumberComponent';
import { VariantNameComponent } from '../../components/VariantNameComponent';
import { OrderContainer } from '../../components/OrderContainer';
import { SuitableBikes } from '../../components/SuitableBikes';
import {
    changeAddToCartButtonActionSku,
    disableAddToCartButton,
    enableAddToCartButton,
    updateAddToCartButtonRecommendationUrl,
} from './addToCartButton';
import * as React from 'react';
import MiddlewareEventBus from '../../../react/common/lib/MiddlewareEventBus';
import { MediaCenterEvent } from '../../../react/common/events/mediaCenterEvent';
import {
    changeLocalStoreAvailabilityButtonConcreteId, disableLocalStoreAvailabilityButton,
} from 'PyzShopUi/scripts/product-detail-page/includes/variant-helper/localStoreAvailabilityButton';
import { TooltipEvent } from 'PyzShopUi/scripts/react/common/events/tooltipEvent';
import ProductVariantDataHandler from 'PyzShopUi/scripts/product-detail-page/includes/productVariantData';
import {
    displayAbstractMediaCenterWishlistIcon,
    displayConcreteMediaCenterWishlistIcon
} from 'PyzShopUi/scripts/product-detail-page/includes/variant-helper/mediaCenterWishlistIcon';
import {
    addProductConcreteFilterParameter
} from 'PyzShopUi/scripts/product-detail-page/includes/productDetailPageUrlParamHelper';
import IReactEvent from 'PyzShopUi/scripts/interfaces/article-list/IReactEvent';

import { B2B_DOMAIN } from 'PyzShopUi/scripts/constants/common';
import { B2BOrderContainer } from 'PyzShopUi/scripts/product-detail-page/components/B2BOrderContainer';
import {
    removeProductAvailability
} from 'PyzShopUi/scripts/product-detail-page/includes/variant-helper/productAvailability';
import { displayImportantNotes } from 'PyzShopUi/scripts/product-detail-page/includes/variant-helper/pdpImportantNotes';
import {
    displayMediaCenterVideoIcon
} from 'PyzShopUi/scripts/product-detail-page/includes/variant-helper/mediaCenterVideoIcon';
import {
    displayMediaCenterSoundIcon
} from 'PyzShopUi/scripts/product-detail-page/includes/variant-helper/mediaCenterSoundIcon';
import Loading from 'PyzShopUi/scripts/react/common/components/Loading';

import { enableGiftCardProductOption } from 'PyzShopUi/scripts/product-detail-page/includes/option-configurator/giftCard';
import DocumentContainer from 'PyzShopUi/scripts/utils/DocumentContainer';
import ProductAvailabilityInfo from 'PyzShopUi/components/molecules/product-availability-info/product-availability-info';
import {flushSync} from 'react-dom';

let rootMap = new Map();

const createRootWrapper = (node) => {
    if (!rootMap.has(node)) {
        const root = createRoot(node);
        rootMap.set(node, root);
    }
    return rootMap.get(node);
}

export const createFetchProductApiHandler =
    (apiUrl: string): (concreteId: number) => void => {
        const addToCartFormElement: HTMLFormElement = document.querySelector('add-to-cart-form');
        const isSprykerSapCcosApiActiveElement = document.querySelector('[data-is-sap-ccos-api-active]');
        const isSprykerSapCcosApiActive = isSprykerSapCcosApiActiveElement ? isSprykerSapCcosApiActiveElement.getAttribute('data-is-sap-ccos-api-active') : false;
        let sku = null;
        return (concreteId: number): void => {
            if(isSprykerSapCcosApiActive) {
                const productVariantData = ProductVariantDataHandler.getArticleFilterVariants();
                sku = productVariantData[concreteId]?.variantId;
            }
            else {
                const productVariantData = ProductVariantDataHandler.init();
                sku = productVariantData[concreteId]?.sku;
            }

            if (typeof sku !== 'undefined' && sku !== null) {
                const apiUrlObject = new URL(apiUrl, window.location.origin);
                const searchParams = new URLSearchParams();
                searchParams.set('sku', sku);
                apiUrlObject.search = searchParams.toString();

                fetch(apiUrlObject.toString())
                    .then((response: Response): Promise<any> => response.json())
                    .then((data) => updateProductInformation(addToCartFormElement, data));
            }
        };
    };

export const createUpdateProductInformationFunction = (addToCartFormElement: HTMLFormElement, data: any): () => void => {
    return () => updateProductInformation(addToCartFormElement, data);
};

const updateProductInformation = (addToCartFormElement?: HTMLFormElement, data?: any) => {
    let selectedBike = null;


    if (data?.bikes) {
        const container = document.querySelector('.suitable-bikes');
        const bikes = data.bikes.filter(bike => bike.name.trim().length > 0);
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        if (urlParams.has('filter_bike_id')) {
            selectedBike = data.bikes.filter(bike => bike.external_id === urlParams.get('filter_bike_id'))[0];
        }

        if (container) {
            createRootWrapper(container).render(<React.Suspense fallback={<Loading />}><SuitableBikes bikes={bikes} /></React.Suspense>);
        }
    }

    if (data?.sku) {
        const container = document.querySelector('.order-number-wrapper');
        if (container) {
            createRootWrapper(container).render(<React.Suspense fallback={<Loading />}><OrderNumberComponent orderNumber={data.sku} /></React.Suspense>);
        }
        addProductConcreteFilterParameter(data.sku);
    }
    if (data) {
        const articleTitleElement = document.querySelector('.article-title');
        if (articleTitleElement) {
            articleTitleElement.textContent = data.name || '';
        }

        const productName = data.name || '';
        const seoTitleSecondary = data.seo_title_secondary || '';

        const articleSeoTitle: HTMLHeadingElement = document.querySelector('[data-qa="pdp-description-seo-title"]');

        if (articleSeoTitle) {
            const newArticleSeoTitle = document.createElement('h2');
            newArticleSeoTitle.setAttribute('data-qa', articleSeoTitle.dataset.qa || '');
            newArticleSeoTitle.innerText = productName;

            if (seoTitleSecondary) {
                const span = document.createElement('span');
                span.innerText = ` ${seoTitleSecondary}`;
                newArticleSeoTitle.appendChild(span);
            }

            articleSeoTitle.replaceWith(newArticleSeoTitle);
        }

        const container = document.querySelector('.variant-name');
        if (container) {
            createRootWrapper(container).render(
                <VariantNameComponent
                    variantName={seoTitleSecondary}
                    bikeAssociationName={selectedBike?.bike_association}
                />
            );
        }
    }

    if (data?.current_product_price) {
        const container = document.querySelector('.order-container');
        const domain = document.documentElement.getAttribute('data-share-domain');
        const orderContainerComponents = [];
        const isSprykerSapCcosApiActiveElement = document.querySelector('[data-is-sap-ccos-api-active]');
        const isSprykerSapCcosApiActive = isSprykerSapCcosApiActiveElement ? isSprykerSapCcosApiActiveElement.getAttribute('data-is-sap-ccos-api-active') : false;

        if (container) {
            if (domain == B2B_DOMAIN) {
                orderContainerComponents.push(
                    <React.Suspense fallback={<Loading />}>
                        <B2BOrderContainer
                            amount={data?.current_product_price?.price}
                            originalAmount={data?.current_product_price?.prices?.CATALOG}
                            currency={data?.current_product_price?.currency?.code} basicPrice={data?.basic_price} />
                    </React.Suspense>
                );
            } else {
                orderContainerComponents.push(
                    <React.Suspense fallback={<Loading />}>
                        <OrderContainer
                            amount={data?.current_product_price?.price}
                            originalAmount={data?.current_product_price?.prices?.CATALOG}
                            currency={data?.current_product_price?.currency?.code} basicPrice={data?.basic_price}
                            batteryDeposit={data?.battery_deposit}
                            batteryDepositPrice={data?.battery_deposit_price}
                            available={data?.available}
                            isNew={data?.attributes?.isNew}
                        />
                    </React.Suspense>
                );
            }

            if (isSprykerSapCcosApiActive && data.id_product_concrete) {
                removeProductAvailability();
                const productAvailablity = document.querySelector('.product-availability-container');
                const productAvailabilityContainer: JSX.Element = getProductAvailabilityContainer(data);
                if (!productAvailablity) {

                    orderContainerComponents.push(
                        productAvailabilityContainer
                    );
                } else {
                    const availabilityContainerDiv = document.createElement('div');
                    const root = createRoot(availabilityContainerDiv);

                    flushSync(() => {
                        root.render(productAvailabilityContainer);
                    });
                    container.append(availabilityContainerDiv);
                }
            }
            if (!isSprykerSapCcosApiActive && data.id_product_concrete) {
                const articalVariants = document.querySelector(`.article-all-variants tr[data-concrete-id="${data.id_product_concrete}"]`);
                const availabilityContainer = articalVariants.querySelector(`.product-availability`);
                if (availabilityContainer) {
                    const productAvailablity = document.querySelector('.product-availability-container');

                    const copyAvailabilityContainer = document.importNode(availabilityContainer, true);
                    const copyTooltipHTMLElement: HTMLElement = copyAvailabilityContainer.querySelector('.tooltip [data-tooltip]');
                    copyTooltipHTMLElement.dataset.tooltip = 'order-container-availability-dialog';

                    const copyTooltipContentHTMLElement: HTMLElement = copyAvailabilityContainer.querySelector('.tooltip-content');
                    copyTooltipContentHTMLElement.id = 'order-container-availability-dialog';

                    const middlewareEventBus: MiddlewareEventBus = MiddlewareEventBus.getInstance();
                    middlewareEventBus.publish({
                        type: TooltipEvent.INIT,
                        payload: { element: copyAvailabilityContainer.querySelector('.tooltip-container') }
                    });
                    const availabilityState: HTMLElement = articalVariants.querySelector('[data-availability-state]');
                    const deliveryWeek: HTMLElement = articalVariants.querySelector('[data-delivery-week]');
                    let productAvailabilityState = availabilityState?.dataset?.availabilityState;
                    if(data?.product_concrete_availability?.available_state != null)
                    {
                        productAvailabilityState = data?.product_concrete_availability?.available_state;
                    }
                    if (!productAvailablity) {
                        orderContainerComponents.push(
                            <div className='product-availability-container'>
                                <ProductAvailabilityInfo
                                    sku={data.sku}
                                    availabilityState={productAvailabilityState}
                                    deliveryWeek={deliveryWeek?.dataset?.deliveryWeek}
                                />
                            </div>
                        );
                    } else {
                        removeProductAvailability();
                        const availabilityContainerDiv = document.createElement('div');
                        const root = createRoot(availabilityContainerDiv);
                        const productAvailabilityContainer: JSX.Element = getProductAvailabilityContainer(data);
                        flushSync(() => {
                            root.render(productAvailabilityContainer);
                        });
                        container.append(availabilityContainerDiv);
                    }
                }
            }
            createRootWrapper(container).render(
                <>
                    {orderContainerComponents.map((orderContainerComponent, index) => (
                        <React.Fragment key={`orderContainer-${index}`}>
                            {orderContainerComponent}
                        </React.Fragment>
                    ))}
                </>
            );
        }
    }

    const media = data.image_sets;

    if (media.length > 0) {
        const documentsContainer = document.querySelector('.article-documents');
        if (documentsContainer) {
            const filterMediaByType = (mediaSets: any, type: string): any[] => mediaSets
                .filter(mediaSet => mediaSet.name === type)
                .reduce((result, mediaSet) => {
                    result.push(...mediaSet.images);
                    // sort documents alphabetically
                    result.sort((firstDocument, secondDocument) => {
                        return firstDocument?.title?.localeCompare(secondDocument?.title);
                    });
                    return result;
                }, []);

            const safetyDataSheets = filterMediaByType(media, 'SafetyDataSheet');
            const manuals = filterMediaByType(media, 'Manual');
            const certificates = filterMediaByType(media, 'Certificate');
            const sounds = filterMediaByType(media, 'Sound');
            const urls = filterMediaByType(media, 'Link');

            const documentRoot = DocumentContainer.createDocumentContainer(documentsContainer);

            const anl = filterMediaByType(media, 'ANL');
            const sic = filterMediaByType(media, 'SIC');
            const snd = filterMediaByType(media, 'SND');
            const tuv = filterMediaByType(media, 'TUV');
            const link = filterMediaByType(media, 'Link');

            const pimsSafetyDataSheets = safetyDataSheets.concat(sic);
            const pimsManuals = manuals.concat(anl);
            const pimsCertificates = certificates.concat(tuv);
            const pimsSounds = sounds.concat(snd);
            const pimsUrls = urls.concat(link);

            documentRoot.render(
                <React.Suspense fallback={<Loading />}>
                    <Documents
                        manuals={pimsManuals}
                        safetyDataSheets={pimsSafetyDataSheets}
                        certificates={pimsCertificates}
                        sounds={pimsSounds}
                        urls={pimsUrls}
                    />
                </React.Suspense>
            );

        }

        displayMediaCenterVideoIcon(media);
        displayMediaCenterSoundIcon(media);
    }

    if (data?.sku) {
        if (addToCartFormElement && data.can_be_ordered) {
            changeAddToCartButtonActionSku(addToCartFormElement, data.sku);
            updateAddToCartButtonRecommendationUrl(
                addToCartFormElement,
                data.sku
            );

            enableAddToCartButton(addToCartFormElement);
        }
        const giftCardInput: HTMLInputElement = document.querySelector('.gift-card-option');
        if (giftCardInput) {
            enableGiftCardProductOption(addToCartFormElement, giftCardInput);
        }

        const eTrackerProduct = {
            id: data.sku,
            name: data.name,
            price: `${data?.current_product_price?.price > 0 ? data?.current_product_price?.price / 100 : 0}`,
            currency: data?.current_product_price?.currency?.code
        }
        displayConcreteMediaCenterWishlistIcon(data.sku, eTrackerProduct);
    }

    if (data?.id_product_concrete) {
        changeLocalStoreAvailabilityButtonConcreteId(data.id_product_concrete);

        displayImportantNotes(data.important_notes);

        const filterSlidesFunction = (swiperSlide: HTMLElement) => data.id_product_concrete === parseInt(swiperSlide.dataset.idConcrete, 10);
        const mediaCenterRenderFilteredSlidesEvent: IReactEvent<any> = {
            type: MediaCenterEvent.RENDER_FILTERED_SLIDES,
            payload: filterSlidesFunction,
        };
        MiddlewareEventBus.getInstance().publish(mediaCenterRenderFilteredSlidesEvent);
    }

    if (data?.sku) {
        const contactFromSku: HTMLInputElement = document.querySelector('#contactForm_subject_article_article_number');
        if (contactFromSku) {
            contactFromSku.value = data.sku;
        }
    }
};
const getProductAvailabilityContainer = (data?: any) => {
    return (
        <div className='product-availability-container'>
            <ProductAvailabilityInfo
                sku={data.sku}
                availabilityState={data?.product_concrete_availability?.availability_state}
                deliveryWeek={data?.product_concrete_availability?.delivery_week}
            />
        </div>
    );
}
export const createSelectFilterHandler = (addToCartFormElement: HTMLFormElement) => (selectedConcretes: Set<number>) => {
    const filterSlidesFunction = (swiperSlide: HTMLElement) => selectedConcretes.has(parseInt(swiperSlide.dataset.idConcrete, 10)) || JSON.parse(swiperSlide.dataset.additionalConcreteIds).some((elem) => {
        return selectedConcretes.has(parseInt(elem, 10));
    });

    MiddlewareEventBus.getInstance().publish({
        type: MediaCenterEvent.RENDER_FILTERED_SLIDES,
        payload: filterSlidesFunction
    });

    resetVariantSelection(addToCartFormElement);
};

export const resetVariantSelection = (addToCartFormElement: HTMLFormElement) => {
    disableAddToCartButton(addToCartFormElement);
    removeProductAvailability();
    disableLocalStoreAvailabilityButton();
    displayAbstractMediaCenterWishlistIcon();
};
